/**
 * Centralized Z-Index Constants
 *
 * This file defines all z-index values used throughout the application.
 * All components should use these constants instead of hardcoded values.
 *
 * Hierarchy (from lowest to highest):
 * - BASE: Basic elements
 * - CONTENT: Content layers
 * - INTERACTIVE: Interactive elements
 * - HEADER: Header elements
 * - FORM: Form components and inputs
 * - DROPDOWN: Dropdown menus and selectors
 * - MODAL: Modal dialogs and overlays
 * - TOP: Top-level elements
 */

export const Z_INDEX = {
  // Base elements
  BASE: 1,

  // Content layers
  CONTENT: 10,
  CARD: 20,

  // Interactive elements
  BUTTON: 100,
  TOOLTIP: 200,
  POPOVER: 300,

  // Header elements (below form components)
  HEADER: 1000,
  HEADER_ICONS: 1100,

  // Form components and inputs (above headers)
  FORM_INPUT: 1200,
  FORM_FIELD: 1250,

  // Dropdown menus and selectors (above forms, below modals)
  DROPDOWN: 1400,
  FORM_DROPDOWN: 1450,
  HEADER_DROPDOWN: 1500,

  // Modal dialogs and overlays
  MODAL: 1600,
  DRAWER: 1650,
  NOTIFICATION: 1675,

  // Main overlay (above everything else)
  OVERLAY: 3000,
  MAX: 99999, // Maximum allowed z-index
} as const;

export type ZIndexLevel = keyof typeof Z_INDEX;

/**
 * Helper function to get z-index value
 * @param level - The z-index level to retrieve
 * @returns The z-index value
 */
export const getZIndex = (level: ZIndexLevel): number => Z_INDEX[level];

/**
 * Helper function to get z-index value with offset
 * @param level - The z-index level to retrieve
 * @param offset - Offset to add to the base z-index
 * @returns The z-index value with offset
 */
export const getZIndexWithOffset = (
  level: ZIndexLevel,
  offset: number
): number => Z_INDEX[level] + offset;
